import { wrapCreateBrowserRouterV7 } from '@sentry/react';
import { type ElementType, type ReactElement } from 'react';
import {
  Navigate,
  createBrowserRouter,
  type LoaderFunction,
  type RouteObject,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';

import AppLayout from './AppLayout';
import { authenticatedLoader } from './auth/authenticatedLoader';
import { ProtectedRoute } from './components/ProtectedRoute';
import {
  organizationLoader,
  projectLoader,
  productLoader,
  userLoader,
  contactLoader,
  osProjectLoader,
  caasClusterLoader,
} from './loaders/routeLoaders';
import AboutUs from './pages/AboutUs';
import Account from './pages/Account';
import AccountOverview from './pages/Account/AccountOverview';
import Notifications from './pages/Account/Notifications';
import Security from './pages/Account/Security';
import AuthCallback from './pages/AuthCallback';
import ContactSecurity from './pages/ContactSecurity';
import ContactUs from './pages/ContactUs';
import DataPrivacy from './pages/DataPrivacy';
import Documentation from './pages/Documentation';
import FallBackError from './pages/Error/FallBackError';
import Page404 from './pages/Error/Page404';
import GiveUsFeedback from './pages/GiveUsFeedback';
import GovernanceTabs from './pages/Governance';
import Gtac from './pages/GTAC';
import Home from './pages/Home/Home';
import OnboardingAdminReview from './pages/Onboarding/OnboardingAdminReview';
import OnboardingPending from './pages/Onboarding/OnboardingPending';
import OnboardingRejected from './pages/Onboarding/OnboardingRejected';
import OnboardingUsers from './pages/Onboarding/OnboardingUsers';
import OnboardingWelcome from './pages/Onboarding/OnboardingWelcome';
import OrganizationDetails from './pages/OrganizationDetails';
import Overview from './pages/OrganizationDetails/Overview';
import Projects from './pages/OrganizationDetails/Projects';
import Project from './pages/OrganizationDetails/Projects/Project';
import Users from './pages/OrganizationDetails/Users';
import UserDetails from './pages/OrganizationDetails/Users/UserDetails';
import PasswordReset from './pages/PasswordReset/PasswordReset';
import UserPasswordUpdate from './pages/PasswordReset/UserPasswordUpdate';
import BareMetalSpecification from './pages/Products/BareMetal/Specification';
import CaasSpecification from './pages/Products/Caas/Specification';
import ComputeOrder from './pages/Products/Compute/ComputeOrder';
import Evaluation from './pages/Products/Compute/Evaluation';
import OsProject from './pages/Products/Compute/OsProject';
import ComputeSpecification from './pages/Products/Compute/Specification';
import ProductDetails from './pages/Products/UserProducts/ProductDetails';
import CaasClusterDetails from './pages/Products/UserProducts/ProductDetails/Caas/CaasClusterDetails';
import CaasClusters from './pages/Products/UserProducts/ProductDetails/Caas/CaasClusters';
import UserProducts from './pages/Products/UserProducts/ProductDetails/Compute';
import Reports from './pages/Reports';
import ContactDetails from './pages/Reports/ContactDetails';
import JuiceGroups from './pages/Reports/JuiceGroups';
import UserActivation from './pages/UserActivation';
import WhatsNew from './pages/WhatsNew';

const lazyRoute = (element: ReactElement): ReactElement => element;

const protectedLazyRoute = (permission: string, component: ElementType): ReactElement =>
  lazyRoute(
    <ProtectedRoute
      permission={permission}
      component={component}
    />
  );

// `protectedLazyRoute` has no loader of its own to wrap in
// `authenticatedLoader`, so without this, a missing MSAL account never
// triggers a login redirect - `ProtectedRoute` just renders null forever.
const noopLoader: LoaderFunction = () => null;

// Skips revalidating `organizationLoader` only for a same-path search-param
// change (e.g. a persisted search term) - anything else (the :organizationId
// param itself changing, an action, a path change) still goes through
// React Router's own default revalidation behavior.
const skipRevalidationForSearchParamsOnly = ({
  currentParams,
  nextParams,
  currentUrl,
  nextUrl,
  formMethod,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean => {
  if (currentParams.organizationId !== nextParams.organizationId) {
    return true;
  }
  // `formMethod` is only set when a mutation (action) triggered this
  // revalidation - trust the default behavior then, even if the search
  // string also happens to differ, rather than suppressing it.
  if (
    !formMethod &&
    currentUrl.pathname === nextUrl.pathname &&
    currentUrl.search !== nextUrl.search
  ) {
    return false;
  }
  return defaultShouldRevalidate;
};

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: lazyRoute(<FallBackError />),
    children: [
      { index: true, element: lazyRoute(<Home />) },
      {
        path: 'onboarding/welcome',
        element: lazyRoute(<OnboardingWelcome />),
        handle: { requiresAuth: true },
      },
      {
        path: 'onboarding/pending',
        element: lazyRoute(<OnboardingPending />),
        handle: { requiresAuth: true },
      },
      {
        path: 'onboarding/rejected',
        element: lazyRoute(<OnboardingRejected />),
        handle: { requiresAuth: true },
      },
      {
        path: 'onboarding/admin-review',
        element: protectedLazyRoute('/onboarding/admin-review', OnboardingAdminReview),
        loader: authenticatedLoader(noopLoader),
        handle: { requiresAuth: true },
      },
      {
        path: 'onboarding/users',
        element: lazyRoute(<OnboardingUsers />),
        handle: { requiresAuth: true },
      },
      { path: 'auth/callback', element: <AuthCallback /> },
      { path: 'new-password/:token', element: <UserPasswordUpdate /> },
      { path: 'reset-password', element: <PasswordReset /> },
      { path: 'user/activate/:token', element: <UserActivation /> },
      {
        path: 'account',
        element: lazyRoute(<Account />),
        handle: { requiresAuth: true },
        children: [
          { index: true, element: <AccountOverview /> },
          { path: 'security', element: <Security /> },
        ],
      },
      { path: 'account/notifications', element: <Notifications />, handle: { requiresAuth: true } },
      { path: 'about-us', element: lazyRoute(<AboutUs />) },
      { path: 'contact-sec', element: lazyRoute(<ContactSecurity />) },
      { path: 'contact-us', element: lazyRoute(<ContactUs />) },
      { path: 'data-privacy', element: lazyRoute(<DataPrivacy />) },
      { path: 'gtc', element: lazyRoute(<Gtac />) },
      {
        path: 'docs/:pageTitle',
        element: lazyRoute(<Documentation />),
        handle: { requiresAuth: true },
      },
      {
        path: 'whats-new',
        element: lazyRoute(<WhatsNew />),
        handle: { requiresAuth: true },
      },
      { path: 'support-and-feedback', element: lazyRoute(<GiveUsFeedback />) },
      {
        path: 'products',
        handle: { requiresAuth: true },
        children: [
          {
            path: 'compute',
            children: [
              {
                index: true,
                element: lazyRoute(
                  <UserProducts
                    key='compute'
                    type='compute'
                  />
                ),
              },
              {
                path: 'free-trial',
                element: protectedLazyRoute('/products/compute/free-trial', Evaluation),
                loader: authenticatedLoader(noopLoader),
              },
              {
                path: 'order',
                element: protectedLazyRoute('/products/compute/order', ComputeOrder),
                loader: authenticatedLoader(noopLoader),
              },
              {
                path: ':productId',
                element: lazyRoute(<ProductDetails />),
                loader: authenticatedLoader(productLoader),
              },
              {
                path: ':productId/osprojects/:juiceGroup/:cloudId/:osprojectName',
                element: lazyRoute(<OsProject />),
                loader: authenticatedLoader(osProjectLoader),
              },
            ],
          },
          {
            path: 'caas-on-openstack',
            children: [
              {
                index: true,
                element: lazyRoute(
                  <UserProducts
                    key='caas'
                    type='caas'
                  />
                ),
              },
              {
                path: ':productId',
                element: lazyRoute(<ProductDetails />),
                loader: authenticatedLoader(productLoader),
              },
              {
                path: ':productId/caas-clusters/:clusterName',
                element: lazyRoute(
                  <CaasClusterDetails
                    clusterData={{
                      cloud_id: '',
                      created: '',
                      device_count: 0,
                      juice_group: '',
                      last_updated: '',
                      name: '',
                      owner: '',
                      type: '',
                      version: '',
                      status: '',
                    }}
                  />
                ),
                loader: authenticatedLoader(caasClusterLoader),
              },
            ],
          },
          {
            path: 'caas-clusters',
            element: protectedLazyRoute('/products', CaasClusters),
            loader: authenticatedLoader(noopLoader),
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: authenticatedLoader(caasClusterLoader),
          },
          {
            path: 'baremetal',
            children: [
              {
                index: true,
                element: lazyRoute(
                  <UserProducts
                    key='baremetal'
                    type='baremetal'
                  />
                ),
              },
              {
                path: ':productId',
                element: lazyRoute(<ProductDetails />),
                loader: authenticatedLoader(productLoader),
              },
            ],
          },
        ],
      },
      {
        path: 'products/:productId',
        handle: { requiresAuth: true },
        children: [
          {
            index: true,
            element: lazyRoute(<ProductDetails />),
            loader: authenticatedLoader(productLoader),
          },
          {
            path: 'contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: authenticatedLoader(contactLoader),
          },
          {
            path: 'osprojects/:juiceGroup/:cloudId/:osprojectName',
            element: lazyRoute(<OsProject />),
            loader: authenticatedLoader(osProjectLoader),
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: authenticatedLoader(caasClusterLoader),
          },
        ],
      },
      { path: 'products/compute/specification', element: lazyRoute(<ComputeSpecification />) },
      { path: 'products/caas/specification', element: lazyRoute(<CaasSpecification />) },
      { path: 'products/baremetal/specification', element: lazyRoute(<BareMetalSpecification />) },
      {
        path: 'organizations',
        handle: { requiresAuth: true },
        element: (
          <Navigate
            to='/governance/organizations'
            replace
          />
        ),
      },
      {
        path: 'governance',
        handle: { requiresAuth: true },
        element: (
          <Navigate
            to='/governance/organizations'
            replace
          />
        ),
      },
      {
        path: 'governance/organizations',
        element: lazyRoute(<GovernanceTabs />),
        handle: { requiresAuth: true },
      },
      {
        path: 'governance/users',
        element: lazyRoute(<GovernanceTabs />),
        handle: { requiresAuth: true },
      },
      {
        path: 'governance/organizations/:organizationId',
        element: lazyRoute(<OrganizationDetails />),
        loader: authenticatedLoader(organizationLoader),
        shouldRevalidate: skipRevalidationForSearchParamsOnly,
        handle: { requiresAuth: true },
        children: [
          { index: true, element: lazyRoute(<Overview />) },
          { path: 'users', element: lazyRoute(<Users />) },
          {
            path: 'users/:uuid',
            element: lazyRoute(<UserDetails />),
            loader: authenticatedLoader(userLoader),
          },
          { path: 'projects', element: lazyRoute(<Projects />) },
          {
            path: 'products',
            element: lazyRoute(
              <UserProducts
                key='governance-all'
                type='all'
              />
            ),
          },
        ],
      },
      {
        path: 'organizations/:organizationId',
        element: lazyRoute(<OrganizationDetails />),
        loader: authenticatedLoader(organizationLoader),
        shouldRevalidate: skipRevalidationForSearchParamsOnly,
        handle: { requiresAuth: true },
        children: [
          { index: true, element: lazyRoute(<Overview />) },
          { path: 'users', element: lazyRoute(<Users />) },
          {
            path: 'users/:uuid',
            element: lazyRoute(<UserDetails />),
            loader: authenticatedLoader(userLoader),
          },
          { path: 'projects', element: lazyRoute(<Projects />) },
          {
            path: 'products',
            element: lazyRoute(
              <UserProducts
                key='org-all'
                type='all'
              />
            ),
          },
        ],
      },
      {
        path: 'users/:uuid',
        element: lazyRoute(<UserDetails />),
        loader: authenticatedLoader(userLoader),
        handle: { requiresAuth: true },
      },
      {
        path: 'projects/:projectId',
        element: lazyRoute(<Project />),
        loader: authenticatedLoader(projectLoader),
        handle: { requiresAuth: true },
      },
      {
        path: 'reports',
        element: lazyRoute(<Reports />),
        handle: { requiresAuth: true },
        children: [
          { path: 'juicegroups', element: lazyRoute(<JuiceGroups />) },
          {
            path: 'juicegroups/contacts/:contactId',
            element: lazyRoute(<ContactDetails />),
            loader: authenticatedLoader(contactLoader),
          },
          {
            path: 'caas-clusters',
            element: protectedLazyRoute('/reports', CaasClusters),
            loader: authenticatedLoader(noopLoader),
          },
          {
            path: 'caas-clusters/:clusterName',
            element: lazyRoute(
              <CaasClusterDetails
                clusterData={{
                  cloud_id: '',
                  created: '',
                  device_count: 0,
                  juice_group: '',
                  last_updated: '',
                  name: '',
                  owner: '',
                  type: '',
                  version: '',
                  status: '',
                }}
              />
            ),
            loader: authenticatedLoader(caasClusterLoader),
          },
        ],
      },
    ],
  },
  { path: '*', element: lazyRoute(<Page404 />) },
];

const configuredBaseUrl = import.meta.env.VITE_BASE_URL as string | undefined;
const normalizedAppPath = (configuredBaseUrl ?? '/').replace(/\/$/, '');

export const appPath = normalizedAppPath.length > 0 ? normalizedAppPath : '/';

function createBaseRouter() {
  const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);
  return sentryCreateBrowserRouter(routes, {
    basename: appPath,
  });
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

export function createAppRouter() {
  return sentryCreateBrowserRouter(routes, {
    basename: appPath,
  });
}

export { createBaseRouter };
